import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Search, Trash2, Upload } from 'lucide-react'

import { assetsApi } from '@/api/assets'
import type { SilkAsset, SilkAssetKind } from '@/api/types'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { CATALOG_LIST_GRID_CLASS } from '@/shared/components/catalog/catalogListGrid'
import { CatalogListSkeleton } from '@/shared/components/catalog/CatalogListSkeleton'
import { CatalogListToolbar } from '@/shared/components/catalog/CatalogListToolbar'
import { PageEmptyState, PageEmptyStateActionButton } from '@/shared/components/PageEmptyState'
import { CreativeLibraryTabs } from '@/features/studio/components/CreativeLibraryTabs'
import { StudioHubPageShell } from '@/features/studio/components/StudioHubPageShell'
import { ProtoBtn, ProtoInput, ProtoPagination, ProtoTabs } from '@/features/studio/components/ui-kit'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { CreativeAssetCard } from '@/features/studio/pages/creative/CreativeAssetCard'
import { useClientPagination } from '@/features/studio/hooks/useClientPagination'

const PAGE_SIZE = 20
const FETCH_LIMIT = 200

type MediaTab = 'all' | 'image' | 'video' | 'audio' | 'other'

function matchesTab(asset: SilkAsset, tab: MediaTab): boolean {
  if (tab === 'all') return true
  if (tab === 'other') return asset.kind === 'other' || asset.kind === 'doc'
  return asset.kind === tab
}

export function CreativeAssetsPage() {
  const { t } = useTranslation()
  const [items, setItems] = useState<SilkAsset[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mediaTab, setMediaTab] = useState<MediaTab>('all')
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [uploading, setUploading] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<SilkAsset | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(searchInput.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await assetsApi.list({ limit: FETCH_LIMIT, offset: 0 })
      setItems(res.items ?? [])
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'creative.assets.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase()
    return items.filter((a) => {
      if (!matchesTab(a, mediaTab)) return false
      if (!q) return true
      return (a.filename || '').toLowerCase().includes(q)
    })
  }, [items, mediaTab, searchQuery])

  const { page, setPage, total, items: pageItems } = useClientPagination(filtered, PAGE_SIZE)

  useEffect(() => {
    setPage(0)
  }, [searchQuery, mediaTab, setPage])

  async function handleUpload(files: FileList | null) {
    const file = files?.[0]
    if (!file) return
    setUploading(true)
    setError(null)
    try {
      const kind: SilkAssetKind | undefined = file.type.startsWith('image/')
        ? 'image'
        : file.type.startsWith('video/')
          ? 'video'
          : file.type.startsWith('audio/')
            ? 'audio'
            : undefined
      await assetsApi.upload(file, kind)
      await reload()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'assets.uploadFailed'))
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return
    setDeleteBusy(true)
    setError(null)
    try {
      await assetsApi.delete(deleteTarget.id)
      setDeleteTarget(null)
      await reload()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'creative.assets.deleteFailed'))
    } finally {
      setDeleteBusy(false)
    }
  }

  const mediaTabs = useMemo(
    () =>
      [
        { id: 'all' as const, label: t('assets.tab.all') },
        { id: 'image' as const, label: t('assets.tab.image') },
        { id: 'video' as const, label: t('assets.tab.video') },
        { id: 'audio' as const, label: t('assets.tab.audio') },
        { id: 'other' as const, label: t('assets.tab.other') },
      ] satisfies { id: MediaTab; label: string }[],
    [t],
  )

  return (
    <>
      <StudioHubPageShell mode="creative">
        <CreativeLibraryTabs active="assets" />
        <CatalogListToolbar
          actions={
            <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto">
              <div className="relative min-w-[200px] flex-1 max-w-xs">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                <ProtoInput
                  className="pl-9"
                  placeholder={t('creative.assets.searchPlaceholder')}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <ProtoBtn size="sm" variant="secondary" disabled={uploading} onClick={() => fileRef.current?.click()}>
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {t('creative.assets.upload')}
              </ProtoBtn>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/*,audio/*"
                className="hidden"
                onChange={(e) => void handleUpload(e.target.files)}
              />
            </div>
          }
        />

        <ProtoTabs tabs={mediaTabs} active={mediaTab} onChange={setMediaTab} />

        {error ? <p className="mb-4 mt-4 text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}

        <div className="mt-4">
          {loading ? (
            <CatalogListSkeleton count={10} />
          ) : filtered.length === 0 ? (
            <PageEmptyState
              icon={Upload}
              tone="violet"
              title={mediaTab === 'all' && !searchQuery ? t('creative.assets.emptyTitle') : t('assets.emptyTab')}
              subtitle={mediaTab === 'all' && !searchQuery ? t('creative.assets.empty') : undefined}
            >
              {mediaTab === 'all' && !searchQuery ? (
                <PageEmptyStateActionButton tone="amber" onClick={() => fileRef.current?.click()}>
                  <Upload className="h-4 w-4" />
                  {t('creative.assets.upload')}
                </PageEmptyStateActionButton>
              ) : null}
            </PageEmptyState>
          ) : (
            <>
              <div className={CATALOG_LIST_GRID_CLASS}>
                {pageItems.map((asset) => (
                  <CreativeAssetCard
                    key={asset.public_id || asset.id}
                    asset={asset}
                    actions={
                      <ProtoBtn
                        size="xs"
                        variant="soft"
                        tone="rose"
                        className="w-full"
                        onClick={() => setDeleteTarget(asset)}
                      >
                        <Trash2 className="h-3 w-3 shrink-0" />
                        <span className="truncate">{t('creative.assets.delete')}</span>
                      </ProtoBtn>
                    }
                  />
                ))}
              </div>
              <ProtoPagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
            </>
          )}
        </div>
      </StudioHubPageShell>

      <ConfirmDialog
        open={deleteTarget != null}
        title={t('creative.assets.deleteTitle')}
        message={t('creative.assets.deleteConfirm', { name: deleteTarget?.filename || deleteTarget?.public_id || '' })}
        confirmLabel={t('common.delete')}
        destructive
        busy={deleteBusy}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  )
}
